import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import { PayCommissionDefaultRateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';

import AutocompleteField from '~components/FormFields/v2/Autocomplete/AutocompleteField';
import NumberInput from '~components/FormFields/v2/NumberInput';
import { PayCommissionDefault } from '~hooks/usePayCommissionDefaults/models';

import {
  COMMISSION_PERCENTAGE_DECIMAL_PLACES,
  CommissionDefaultDtoAsyncError,
  CommissionDefaultFormData,
  CommissionDefaultFormValues,
  commissionDefaultSchema,
  commissionRateTypeLabel,
  MAX_COMMISSION_PERCENTAGE,
} from './commissionDefaultSchema';
import CommissionPayeeSelector from './CommissionPayeeSelector';

export type CommissionDefaultFormHandler = {
  onSubmit: () => Promise<CommissionDefaultFormData | undefined>;
};

const setCommissionDefaultSchemaDefaultValues = (
  commissionDefault?: PayCommissionDefault,
): CommissionDefaultFormValues => {
  if (!commissionDefault) {
    return { payee: null, percentage: '', rateType: null };
  }

  return {
    payee: {
      label: commissionDefault.payeeName,
      value: commissionDefault.driverId ?? commissionDefault.vendorAccountId ?? '',
    },
    percentage: commissionDefault.percentage,
    rateType: {
      label: commissionRateTypeLabel(commissionDefault.rateType),
      value: commissionDefault.rateType,
    },
  };
};

interface CommissionDefaultFormProps {
  asyncErrors?: CommissionDefaultDtoAsyncError[];
  commissionDefault?: PayCommissionDefault;
  companyId: string;
  isDriver: boolean;
}

const CommissionDefaultForm = forwardRef<
  CommissionDefaultFormHandler,
  CommissionDefaultFormProps
>(function CommissionDefaultForm(
  { asyncErrors, commissionDefault, companyId, isDriver },
  ref,
) {
  const isEditing = Boolean(commissionDefault);

  const { handleSubmit, control, setError, clearErrors } =
    useForm<CommissionDefaultFormValues>({
      resolver: yupResolver(commissionDefaultSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: setCommissionDefaultSchemaDefaultValues(commissionDefault),
    });

  useImperativeHandle(
    ref,
    () => ({
      onSubmit() {
        return new Promise((resolve) => {
          handleSubmit(
            (data) => {
              resolve(commissionDefaultSchema.cast(data));
            },
            () => {
              resolve(undefined);
            },
          )();
        });
      },
    }),
    [handleSubmit],
  );

  useEffect(() => {
    if (asyncErrors) {
      for (const error of asyncErrors) {
        setError(error.field, { message: error.message });
      }
    }

    return () => {
      clearErrors();
    };
  }, [asyncErrors, clearErrors, setError]);

  const rateTypeOptions = Object.values(PayCommissionDefaultRateType).map((type) => ({
    _entity: type,
    label: commissionRateTypeLabel(type),
    value: type,
  }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}>
      <CommissionPayeeSelector
        companyId={companyId}
        control={control}
        isDisabled={isEditing}
        isDriver={isDriver}
      />
      <AutocompleteField
        control={control}
        disableClearable
        isDisabled={isEditing}
        isRequired
        label={<label htmlFor="rateType">{t('form_fields.rate_type')}</label>}
        name="rateType"
        options={rateTypeOptions}
      />
      <NumberInput
        control={control}
        isRequired
        label={t('pay_commission.percentage_label')}
        name="percentage"
        slotProps={{
          input: { endAdornment: '%' },
          htmlInput: {
            min: 0,
            max: MAX_COMMISSION_PERCENTAGE,
            step: 10 ** -COMMISSION_PERCENTAGE_DECIMAL_PLACES,
            'aria-label': t('pay_commission.percentage_label'),
          },
        }}
      />
    </Box>
  );
});

export default CommissionDefaultForm;
